
import React, { useEffect, useRef, useState, type DragEvent } from 'react'
import Card from './Card'
import type { TagLike } from '@univdiam/ui/lib/tagColors'
import DropIndicator from './DropIndicator'
import { Separator } from '../../components/separator'
import { Loader2 } from 'lucide-react'
import FeaturedIcon from './FeaturedIcon'
import { ScrollArea } from '../../components/scroll-area'
import type { CardType } from '@univdiam/ui/type'

type ColumnProps = {
  title: string
  headingColor: string
  cards: CardType[]
  column: string
  setCards: React.Dispatch<React.SetStateAction<CardType[]>>,
  isDrag: boolean;
  handleOrderOpen: (id: string, customer_id: string, order_ref: string) => void;
  updateOrderStatus?: (data: { order_id: string, customer_id: string, status: string, orderRef: string }) => void;
  isAdmin?: boolean;
  adminTags?: TagLike[];
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
}

const Column: React.FC<ColumnProps> = ({ title, cards, column, setCards, isDrag, handleOrderOpen, updateOrderStatus, isAdmin, adminTags = [], fetchNextPage, hasNextPage, isFetchingNextPage }) => {

  const [active, setActive] = useState(false)
  const [filteredCards, setFilteredCards] = useState<CardType[]>([])
  const handleDragStart = (e: MouseEvent | TouchEvent | PointerEvent, card: CardType) => {
    const dragEvent = e as unknown as DragEvent
    dragEvent.dataTransfer?.setData("cardId", card.id)
  }


  const handleDragEnd = (e: DragEvent<HTMLDivElement>) => {
    const cardId = e.dataTransfer.getData("cardId")
    setActive(false)
    clearHighlights()

    const indicators = getIndicators()
    const { element } = getNearestIndicator(e, indicators)
    const before = element.dataset.before || "-1"

    const cardToMove = cards.find((c) => c.id.toString() === cardId.toString())

    setCards(prev => {
      const cardInPrev = prev.find((c) => c.id.toString() === cardId.toString())
      if (!cardInPrev) return prev

      // Remove it from previous column
      const updated = prev.filter((c) => c.id.toString() !== cardId.toString())

      const updatedCard = { ...cardInPrev, column }

      const moveToBack = before === "-1"

      if (moveToBack) {
        updated.push(updatedCard)
      } else {

        const insertAtIndex = updated.findIndex((c) =>
          c.id.toString() === before && c.column === column
        )
        if (insertAtIndex === -1) {

          updated.push(updatedCard)
        } else {
          updated.splice(insertAtIndex, 0, updatedCard)
        }
      }

      return updated
    })

    if (cardToMove && cardToMove.column !== column && updateOrderStatus) {
      updateOrderStatus({
        order_id: cardToMove.order_id,
        customer_id: cardToMove.customer_id.toString(),
        status: column, // column === new status
        orderRef: cardToMove.id.toString()
      })
    }
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    highlightIndicator(e)
    setActive(true)
  }

  const clearHighlights = (els?: HTMLElement[]) => {
    const indicators = els || getIndicators()
    indicators.forEach((i) => {
      i.style.opacity = "0"
    })
  }

  const highlightIndicator = (e: DragEvent<HTMLDivElement>) => {
    const indicators = getIndicators()
    clearHighlights(indicators)

    const el = getNearestIndicator(e, indicators)
    el.element.style.opacity = "1"
  }

  const getNearestIndicator = (
    e: DragEvent,
    indicators: HTMLElement[]
  ): { offset: number; element: HTMLElement } => {
    const DISTANCE_OFFSET = 50

    const el = indicators.reduce(
      (closest, child) => {
        const box = child.getBoundingClientRect()
        const offset = e.clientY - (box.top + DISTANCE_OFFSET)

        if (offset < 0 && offset > closest.offset) {
          return { offset, element: child }
        } else {
          return closest
        }
      },
      {
        offset: Number.NEGATIVE_INFINITY,
        element: indicators[indicators.length - 1]!
      }
    )

    return el
  }

  const getIndicators = (): HTMLElement[] => {
    return Array.from(document.querySelectorAll(`[data-column="${column}"]`)) as HTMLElement[]
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    clearHighlights()
    setActive(false)
  }

  useEffect(() => {
    setFilteredCards(cards.filter((c) => c.column === column))
  }, [cards, column])

  // Infinite scroll: the sentinel sits below the last card, so pulling the next
  // page is just "is the bottom of this column on screen?". Default IO root is
  // the viewport, and intersection is computed through the ancestor clip chain,
  // so this works inside ScrollArea without reaching for its viewport node.
  const sentinelRef = useRef<HTMLDivElement>(null)
  const isFetchingRef = useRef(false)
  isFetchingRef.current = !!isFetchingNextPage

  useEffect(() => {
    const el = sentinelRef.current
    if (!el || !hasNextPage || !fetchNextPage) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && !isFetchingRef.current) fetchNextPage()
      },
      // Load one screen early so scrolling stays continuous instead of
      // stalling at the bottom of the column.
      { rootMargin: '200px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
    // filteredCards.length, and deliberately NOT isFetchingNextPage, is the
    // re-arm signal. An observer that already reported "intersecting" stays
    // quiet until the sentinel crosses the edge again, so re-arming is what
    // keeps pulling pages until the column is tall enough to push the sentinel
    // off screen. Re-arming on every fetch flip instead would chain-load every
    // remaining page of a column whose rows are all hidden by the tag filter,
    // because the sentinel never moves and each new observer fires on sight.
  }, [hasNextPage, fetchNextPage, filteredCards.length])


  return (
    <div className="w-[274px] h-full flex flex-col overflow-hidden shrink-0 bg-[#FCFCFC] rounded-[8px] relative border border-solid border-[#E9EAEB]">
      <div className='sticky top-0 z-10'>
        <div className="flex items-center justify-between px-[16px] py-[12px]">
          <div className='flex items-center gap-[8px] text-body2'>
            <FeaturedIcon />
            <h3>{title}</h3>
          </div>
          {/* <span className="rounded text-sm text-neutral-400">{filteredCards.length}</span> */}
        </div>
        <Separator className='bg-[#E9EAEB]' />
      </div>

      <ScrollArea className="flex-1 min-h-0 px-[16px] py-[12px]">
        <div
          onDrop={(e) => {
            if (!isDrag) {
              return
            }
            handleDragEnd(e)
          }}
          onDragOver={(e) => {
            if (!isDrag) {
              return
            }
            handleDragOver(e)
          }}
          onDragLeave={(e) => {
            if (!isDrag) {
              return
            }
            handleDragLeave(e)
          }}
          className={`min-h-full flex flex-col space-y-[12px] transition-colors ${active ? "bg-neutral-200/50" : "bg-neutral-800/0"
            }`}
        >
          {filteredCards?.map((c) => (
            <Card key={c.id} data={c} handleDragStart={handleDragStart} handleOrderOpen={handleOrderOpen} isDrag={isDrag} isAdmin={isAdmin} adminTags={adminTags} />
          ))}
          <DropIndicator beforeId={null} column={column} />
          {/* <AddCard column={column} setCards={setCards} /> */}
          
          {hasNextPage && fetchNextPage && (
            <div ref={sentinelRef} className="w-full flex justify-center py-2 pb-4">
              {isFetchingNextPage && (
                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              )}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>

  )
}

export default Column
